<script lang="ts">
	import type { PartnerBoxMapContent, PartnerBoxmapTrackingData } from '$lib/components/advertising/partners/types';
	import { page } from '$app/state';
	import { AdvertisingPartnerType, type ValueOf } from '$houdini';
	import PartnerBoxMap from '$lib/components/advertising/partners/boxmap/PartnerBoxMap.svelte';
	import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
	import { getSelectedAddress } from '$lib/components/map/selectedAddress.svelte';
	import { createBookingcomUrlWithLabel, removeTimestampFromLabelOnBookingcomUrl } from '$lib/services/bookingcom/urlWithLabel';
	import { isMobile } from '$lib/services/mediaQuery/screenSize';
	import { isPartnerBoxmapContext } from '$lib/services/store/partnerBoxmaps';
	import { trackPartnerBoxmapClick, trackPartnerBoxmapLoaded } from '$lib/services/tracking/googleAnalytics/partners';
	import { getSearchParamsStore } from '$lib/services/urls/searchParams';
	import * as m from '$paraglide/messages';
	import { itineraryResultsStoreUtils } from '$routes/(map)/routes/loadItinerary';

	const { partnerBoxMapsAreReduced, partnerBoxMapsContainerExpandedWidth, itineraryStore } = getMapLayoutContext();
	const searchParams = getSearchParamsStore();
	const REDUCE_AFTER_TIME = 10_000;

	const { itineraryResultsStore, boxmapsForItineraryStore, boxmapsForPageStore, waypointsInfo, poisStore } = $derived(page.data);

	let boxmapsContainer: HTMLElement | undefined = $state();
	let computeContainerWidthTimer: number;
	let reduceTimer: number;

	function expandBoxMaps() {
		if (!$partnerBoxMapsAreReduced && boxmapsContainer) {
			$partnerBoxMapsContainerExpandedWidth = 'auto';

			computeContainerWidthTimer = window.setTimeout(() => {
				if (!boxmapsContainer) {
					return;
				}

				$partnerBoxMapsContainerExpandedWidth = `${boxmapsContainer.offsetWidth}px`;
			}, REDUCE_AFTER_TIME - 200);

			reduceTimer = window.setTimeout(() => {
				$partnerBoxMapsAreReduced = true;
			}, REDUCE_AFTER_TIME);
		}
	}

	function clearBoxMapsExpandedTimeouts() {
		clearTimeout(reduceTimer);
		clearTimeout(computeContainerWidthTimer);
	}

	function getTrackingData(boxmapType: ValueOf<typeof AdvertisingPartnerType>): PartnerBoxmapTrackingData {
		const selectedAddress = getSelectedAddress();
		const routeIndex = $searchParams?.selectedRoute ?? 0;
		return {
			searchParams: $searchParams || {},
			routeIndex,
			route: $itineraryResultsStore?.routes[routeIndex] ?? undefined,
			arrivalAddress: $itineraryResultsStore?.arrivalAddress ?? (selectedAddress && {
				city: selectedAddress.city,
				country: selectedAddress.country ? { code: '', name: selectedAddress.country } : null,
				department: selectedAddress.department,
				region: selectedAddress.region,
				zipCode: selectedAddress.zipCode,
			}),
			departureAddress: $itineraryResultsStore?.departureAddress ?? undefined,
			waypoints: $itineraryResultsStore?.waypoints,
			advertiser: boxmapType,
		};
	}

	const bookingBoxmapBaseData = {
		type: AdvertisingPartnerType.BOOKING,
		message: m.map_controls__partner_hotel__long_label(),
		img: 'https://bff.viamichelin.com/static/img/advertising/booking/boxmap/brand-logo_w160.webp',
		shortMessage: m.map_controls__partner_hotel(),
		tracking: '',
	};

	const { itineraryArrival, userLocationCoordinates } = itineraryStore;

	const selectedAddress = getSelectedAddress();

	const boxmaps = $derived.by(() => {
		const hasBoxmapsPageStore = boxmapsForPageStore && $boxmapsForPageStore && ($boxmapsForPageStore?.length ?? 0) > 0;
		const hasBoxmapsItineraryStore = boxmapsForItineraryStore && $boxmapsForItineraryStore && ($boxmapsForItineraryStore?.length ?? 0) > 0;

		let nextBoxmaps: PartnerBoxMapContent[] = [];

		if (hasBoxmapsPageStore) {
			nextBoxmaps = $boxmapsForPageStore.map((boxmapInfo) => {
				if (!isPartnerBoxmapContext(boxmapInfo)) {
					return boxmapInfo;
				}

				if (boxmapInfo.type === AdvertisingPartnerType.BOOKING) {
					let url;
					if (boxmapInfo.context === 'itinerary_search') {
						if ($itineraryArrival && $itineraryArrival.label) {
							url = createBookingcomUrlWithLabel().boxmap_itisearch_with_arrival({
								arrival: $itineraryArrival.coordinates !== 'MY_LOC' ? $itineraryArrival.label : '',
								arrivalCoordinates: $itineraryArrival.coordinates !== 'MY_LOC' ? $itineraryArrival.coordinates : $userLocationCoordinates,
							});
						} else {
							url = createBookingcomUrlWithLabel().boxmap_itisearch_generic();
						}
					} else if (boxmapInfo.context === 'map_result') {
						const arrivalCoordinates = selectedAddress?.mapLocation?.location || $poisStore?.selectedAddress?.coordinates;
						const arrival = selectedAddress?.address?.city || $poisStore?.selectedAddress?.address;
						if (arrival && arrivalCoordinates) {
							url = createBookingcomUrlWithLabel().boxmap_mapresult_with_arrival({
								arrival,
								arrivalCoordinates,
							});
						}
					}

					if (url) {
						return {
							...bookingBoxmapBaseData,
							url,
						};
					}
				}

				return undefined;
			}).filter((v): v is PartnerBoxMapContent => v !== undefined);
		} else if (hasBoxmapsItineraryStore && waypointsInfo && itineraryResultsStore && $itineraryResultsStore) {
			nextBoxmaps = $boxmapsForItineraryStore.map((boxmapInfo: any) => {
				if (boxmapInfo.type === AdvertisingPartnerType.BOOKING) {
					const itinerary = itineraryResultsStoreUtils(itineraryResultsStore);
					const arrivalWaypoint = itinerary.getArrivalWaypoint();
					const departureWaypoint = itinerary.getDepartureWaypoint();
					const arrival = arrivalWaypoint?.city || arrivalWaypoint?.name as string;
					const departure = departureWaypoint?.city || departureWaypoint?.name as string;

					return {
						...bookingBoxmapBaseData,
						url: createBookingcomUrlWithLabel().boxmap_itiresult({
							arrival,
							arrivalCoordinates: arrivalWaypoint?.location,
							departure,
							distance: $itineraryResultsStore?.routes[$searchParams.selectedRoute ?? 0]?.routeDistance?.value,
						}),
					};
				}
				return boxmapInfo;
			});
		}

		return nextBoxmaps;
	});

	$effect(() => {
		if (boxmaps.length > 0 && boxmapsContainer) {
			expandBoxMaps();
		}

		return clearBoxMapsExpandedTimeouts;
	});

	const shouldReduced = $derived($isMobile && $partnerBoxMapsAreReduced);

	// We need to normalize the url to avoid unnecessary re-renders for tracking purposes `onMount`
	function normalizeUrlForKey(boxmap: PartnerBoxMapContent) {
		if (boxmap.type === AdvertisingPartnerType.BOOKING) {
			return removeTimestampFromLabelOnBookingcomUrl(boxmap.url);
		}

		return boxmap.url;
	}
</script>

{#if boxmaps.length > 0}
	<div
		bind:this={boxmapsContainer}
		class={['flex flex-col gap-1 group', shouldReduced && 'justify-end items-end']}
		style="--expanded-container-width: {($isMobile && $partnerBoxMapsContainerExpandedWidth) || 'auto'}"
	>
		{#each boxmaps as boxMap (normalizeUrlForKey(boxMap))}
			{@const isBookingBoxmap = boxMap.type === AdvertisingPartnerType.BOOKING}
			<PartnerBoxMap
				data={boxMap}
				shortMessage={(shouldReduced && isBookingBoxmap && boxMap.shortMessage) || undefined}
				class={`${shouldReduced ? `${(!isBookingBoxmap && 'w-[36px]') || ''} transition-width ease-in-out md:group-hover:w-[--expanded-container-width] overflow-hidden` : 'w-[--expanded-container-width]'}`}
				onclick={() => trackPartnerBoxmapClick(getTrackingData(boxMap.type))}
				onMount={() => trackPartnerBoxmapLoaded(getTrackingData(boxMap.type))}
			/>
		{/each}
	</div>
{/if}
